'use client';

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/forms/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading, FormSectionHeader, HintNote } from '@/components/ui/design-system';
import { ResumeSelector } from '@/components/features/resume/ResumeSelector';
import {
  INITIAL_AUTOFILL_FLOW_STATE,
  autofillFlowReducer,
  buildResumeSourcePayload,
  describeAutofillConfirm,
  type ResumeSourcePayload,
} from '@/lib/resume/autofill-source-flow';

/**
 * How long to wait for a just-saved source to show up in `sourceCount` before
 * giving up. Purely a stuck-spinner guard — a normal React state propagation
 * lands within a frame or two.
 */
const AWAITING_SOURCE_TIMEOUT_MS = 5000;

const DEFAULT_DESCRIPTION =
  'Pick the resume we should read from. We use it to prefill this resume, so one focused source works best.';

export interface AutofillSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Persists the chosen resume as a report source. Wire this to the existing
   * `handleSubmitSource`: the payload is byte-for-byte what the shared sources
   * dialog posts to `/api/resume-builder/{resumeId}/sources` today. Resolve
   * `true` on success.
   */
  onSaveSource: (payload: ResumeSourcePayload) => Promise<boolean>;
  /**
   * Runs the existing autofill (`runFillFromSources`). It owns its own toasts;
   * resolve `true` on success so the dialog knows to close itself.
   */
  onRunAutofill: () => Promise<boolean>;
  /**
   * Number of sources currently persisted on the report (`sources.length`).
   * Load-bearing: `runFillFromSources` reads `sources.length` from its own
   * closure and refuses to run against an empty list, so the dialog holds the
   * run back until the caller can actually see the source it just saved.
   */
  sourceCount: number;
  /** Caller's own in-flight autofill flag (`isFillingFromSources`). */
  isRunningAutofill?: boolean;
  /** Copy override for the dialog subtitle. */
  description?: string;
}

/**
 * Single-purpose Autofill dialog: choose a resume source, confirm, done.
 * Persistence and the autofill run itself are unchanged — this only replaces
 * the multi-tab source workflow that used to sit in front of them.
 */
export function AutofillSourceDialog({ open, onOpenChange, ...bodyProps }: AutofillSourceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent layout="panel" className="min-[640px]:max-w-2xl">
        {/* Dialog content unmounts on close, so the picker's selection and the
            flow state reset themselves without any teardown bookkeeping. */}
        <AutofillSourceDialogBody onOpenChange={onOpenChange} {...bodyProps} />
      </DialogContent>
    </Dialog>
  );
}

type AutofillSourceDialogBodyProps = Omit<AutofillSourceDialogProps, 'open'>;

function AutofillSourceDialogBody({
  onOpenChange,
  onSaveSource,
  onRunAutofill,
  sourceCount,
  isRunningAutofill = false,
  description = DEFAULT_DESCRIPTION,
}: AutofillSourceDialogBodyProps) {
  const [selectedResumeId, setSelectedResumeId] = useState<string | undefined>(undefined);
  const [state, dispatch] = useReducer(autofillFlowReducer, INITIAL_AUTOFILL_FLOW_STATE);

  /**
   * Latest-ref rather than an effect dependency: the caller rebuilds
   * `onRunAutofill` whenever its source list changes, and re-running the effect
   * on a new identity would restart a run that is already in flight.
   */
  const runAutofillRef = useRef(onRunAutofill);
  const closeRef = useRef(onOpenChange);
  const isMountedRef = useRef(true);

  useEffect(() => {
    runAutofillRef.current = onRunAutofill;
    closeRef.current = onOpenChange;
  });

  // Re-arming on mount matters: StrictMode's dev double-mount would otherwise
  // leave the flag stuck at false and swallow every later dispatch.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Feeds the caller's source list into the state machine, which is what
  // releases the run once the saved source has landed.
  useEffect(() => {
    dispatch({ type: 'sources-changed', sourceCount });
  }, [sourceCount, state.phase]);

  useEffect(() => {
    if (state.phase !== 'awaiting-source') return undefined;
    const timer = window.setTimeout(
      () => dispatch({ type: 'awaiting-source-timeout' }),
      AWAITING_SOURCE_TIMEOUT_MS
    );
    return () => window.clearTimeout(timer);
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== 'running') return;
    void (async () => {
      const ok = await runAutofillRef.current();
      if (!isMountedRef.current) return;
      dispatch({ type: 'run-settled', ok });
      if (ok) closeRef.current(false);
    })();
  }, [state.phase]);

  const handleConfirm = useCallback(async () => {
    dispatch({ type: 'confirm', selectedResumeId });
    if (!selectedResumeId) return;
    const ok = await onSaveSource(buildResumeSourcePayload(selectedResumeId));
    if (!isMountedRef.current) return;
    dispatch({ type: 'save-settled', ok });
  }, [onSaveSource, selectedResumeId]);

  const confirm = describeAutofillConfirm({
    state,
    hasSelection: Boolean(selectedResumeId),
    isRunningExternally: isRunningAutofill,
  });

  return (
    <>
      <DialogHeading
        leading={
          <div className="flex size-9 items-center justify-center border border-border/60 bg-muted/30 text-primary">
            <Sparkles className="size-4" />
          </div>
        }
        title="Autofill"
        description={description}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-4 px-4 py-4 min-[640px]:px-6">
          <div className="space-y-4 min-[640px]:rounded-sm min-[640px]:border min-[640px]:border-border min-[640px]:bg-card min-[640px]:p-5">
            <FormSectionHeader
              title="Resume source"
              description="Select an existing resume to use as source context."
            />
            <ResumeSelector selectedResumeId={selectedResumeId} onSelect={setSelectedResumeId} />
          </div>
        </div>
      </div>

      <DialogFooter className="shrink-0 border-t border-border bg-background px-4 py-4 min-[640px]:px-6">
        <div className="flex w-full flex-col gap-3">
          {state.error ? (
            <HintNote className="border-destructive/40 bg-destructive/10 text-destructive">
              {state.error}
            </HintNote>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border"
              style={{ boxShadow: 'var(--shadow-warm-sm)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={confirm.disabled}
              className="border"
              style={{ boxShadow: 'var(--shadow-warm-sm)' }}
            >
              {confirm.busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {confirm.label}
            </Button>
          </div>
        </div>
      </DialogFooter>
    </>
  );
}
